<template>
  <div v-if="lesson" class="lesson-player-container pa-0">
    <div class="header-bar px-4 px-md-6 py-4 d-flex align-center justify-space-between">
      <v-btn icon dark @click="$router.push(`/akademie/modules/${$route.params.id}`)">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div class="text-center flex-grow-1" style="min-width: 0">
        <div class="text-caption grey--text text-uppercase tracking-widest">Lektion {{ (lesson.order || 0) + 1 }}</div>
        <h2 class="text-h6 white--text font-weight-bold text-truncate">{{ lesson.title }}</h2>
      </div>
      <div style="width: 36px"></div>
    </div>

    <v-container fluid class="player-content pa-4 pa-md-8" style="max-width: 1100px">
      <p v-if="lesson.introduction" class="introduction grey--text text--lighten-2 mb-6">{{ lesson.introduction }}</p>

      <!-- Media -->
      <div v-if="lesson.contentType === 'video' || lesson.contentType === 'audio' || lesson.contentType === 'image'" class="media-block mb-8">
        <SecuredMedia
          v-if="streamPath"
          ref="securedMedia"
          :src="streamPath"
          :media-type="lesson.contentType"
          eager />
      </div>

      <!-- Text -->
      <v-card v-if="lesson.contentType === 'text'" class="reader-card pa-6 pa-md-8 rounded-xl mb-8" dark elevation="0">
        <div class="html-content text-body-1" v-html="lesson.textContent"></div>
      </v-card>

    </v-container>

    <!-- Footer Controls -->
    <v-footer fixed class="player-footer px-4 px-md-6 py-3" color="#121212">
      <div class="d-flex align-center justify-end w-100">
        <v-btn color="#D4AF37" class="black--text font-weight-bold" rounded :disabled="!canComplete" @click="completeLesson" :loading="completing">
          <v-icon left>mdi-check-circle</v-icon> Lektion abschliessen
        </v-btn>
      </div>
    </v-footer>

  </div>
</template>

<script>
import api from "@/api"
import SecuredMedia from "./SecuredMedia.vue"

export default {
  name: "LessonPlayer",
  components: { SecuredMedia },
  data() {
    return {
      lesson: null,
      module: null,
      streamPath: "",
      canComplete: false,
      completing: false,
      autoCompleteTimer: null,
    }
  },
  mounted() {
    this.loadLesson()
  },
  beforeDestroy() {
    if (this.autoCompleteTimer) clearTimeout(this.autoCompleteTimer)
  },
  methods: {
    async loadLesson() {
      try {
        const mod = await api.akademie.fetchModule(this.$route.params.id)
        if (!mod) return
        this.module = mod
        if (mod.lessons) {
          this.lesson = mod.lessons.find((l) => l._id === this.$route.params.lessonId)
        }
        if (this.lesson && this.lesson.mediaFile) {
          this.streamPath = api.akademie.getLessonStreamPath(this.lesson._id)
        }
        // Text / Image -> sofort abschliessbar; Video/Audio nach kurzer Mindestdauer
        if (this.lesson) {
          if (this.lesson.contentType === "text" || this.lesson.contentType === "image") {
            this.canComplete = true
          } else {
            // Mindest-Anzeigezeit: 5 Sek (Backup, falls Player keinen ended-Event feuert)
            this.autoCompleteTimer = setTimeout(() => (this.canComplete = true), 5000)
          }
        }
      } catch (err) {
        console.error("Fehler beim Laden der Lektion:", err)
      }
    },
    async completeLesson() {
      this.completing = true
      try {
        await api.akademie.completeLesson(this.$route.params.id, this.lesson._id)
        this.$router.push(`/akademie/modules/${this.$route.params.id}`)
      } catch (err) {
        console.error("Completion error", err)
      } finally {
        this.completing = false
      }
    },
  },
}
</script>

<style scoped>
.lesson-player-container {
  min-height: 100vh;
  background-color: #121212;
  padding-bottom: 90px !important;
}

.header-bar {
  background: rgba(18, 18, 18, 0.85);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: sticky;
  top: 0;
  z-index: 5;
}

.tracking-widest { letter-spacing: 0.1em; }

.player-content { margin: 0 auto; }

.introduction {
  font-size: 1.05rem;
  line-height: 1.7;
}

.media-block :deep(.secured-media-content) {
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.7), 0 0 20px rgba(212, 175, 55, 0.08);
  max-height: 75vh;
}

.reader-card {
  background: #1e1e1e !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  line-height: 1.8;
}

.html-content {
  color: #e0e0e0;
}

.toolbox-section {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  padding: 20px;
}

.toolbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.toolbox-item {
  background: rgba(30, 30, 30, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.toolbox-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}

.toolbox-preview :deep(.secured-media-content) {
  max-height: 120px;
  object-fit: cover;
}

.text-clip-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.w-100 { width: 100%; }

.player-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(18, 18, 18, 0.95) !important;
  backdrop-filter: blur(10px);
}
</style>
